import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Wordmark, th, T, F, Btn } from "@/lib/bricks";
import BricksLoader from "@/components/BricksLoader";
import {
  RoomDates, RoomConditions, RoomDocuments, RoomAgent, RoomAction, RoomBlock, RoomLine, RoomSent, addressLine, fmtInstant,
} from "@/components/deal/RoomView";

// One outside party's page on a deal (lawyer, lender, inspector...). No sign-in: the link is
// the key, it shows only what the agent picked, and the server re-checks that on every load.

const p = th(false);
const CSS = `
.rp-wrap{max-width:1120px;margin:0 auto;padding:0 40px;box-sizing:border-box}
.rp-grid{display:grid;grid-template-columns:minmax(0,1fr);gap:40px}
.rp-long{display:inline}.rp-short{display:none}
@media (min-width:1024px){.rp-grid{grid-template-columns:minmax(0,1.75fr) minmax(0,1fr);gap:72px}}
@media (max-width:719px){.rp-wrap{padding:0 20px}.rp-long{display:none}.rp-short{display:inline}}
`;

function useMeta(name, content) {
  useEffect(() => {
    let el = document.querySelector(`meta[name="${name}"]`);
    const made = !el;
    const prev = el ? el.getAttribute("content") : null;
    if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
    el.setAttribute("content", content);
    return () => { if (made) el.remove(); else el.setAttribute("content", prev); };
  }, [name, content]);
}

const GONE = "It may have expired or been turned off. Ask the agent who sent it for a new one.";
const DEAD = (b) => ({ title: "This link isn't working", body: b?.detail?.message || GONE });

// What a party can send back. Mirrors backend/common/deal_room_handback.py.
const EXT_TYPE = { pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", heic: "image/heic", heif: "image/heic",
  doc: "application/msword", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" };
const ACCEPT = [...Object.keys(EXT_TYPE).map((e) => `.${e}`), ...new Set(Object.values(EXT_TYPE))].join(",");
const MAX_BYTES = 25 * 1024 * 1024;
function fileType(file) {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  const t = (file.type || "").toLowerCase();
  if (Object.values(EXT_TYPE).includes(t)) return t;
  if (t === "image/heif") return "image/heic";
  return EXT_TYPE[ext] || t;
}
function fileProblem(file) {
  if (!Object.values(EXT_TYPE).includes(fileType(file))) return "Send a PDF, a photo (JPG, PNG or HEIC) or a Word document.";
  if (!file.size) return "That file is empty.";
  if (file.size > MAX_BYTES) return "That file is over 25 MB. Send a smaller copy.";
  return "";
}
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function putFile(url, file, type, onProgress) {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open("PUT", url);
    x.setRequestHeader("Content-Type", type);
    x.setRequestHeader("x-upsert", "false");
    x.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    x.onload = () => (x.status >= 200 && x.status < 300 ? resolve() : reject(new Error("upload")));
    x.onerror = () => reject(new Error("upload"));
    x.send(file);
  });
}

const field = { width: "100%", minHeight: 48, boxSizing: "border-box", border: `1px solid ${p.hairS || p.hair}`, borderRadius: 2, background: p.bg,
  color: p.ink, fontFamily: F.display, fontSize: 16, padding: "0 12px" };
const LINK = { background: "none", border: "none", padding: "0 2px", minHeight: 44, display: "inline-flex", alignItems: "center", color: p.ink2, fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: p.hairS || p.hair, whiteSpace: "nowrap" };
const panel = { border: `1px solid ${p.hair}`, borderRadius: 2, padding: 16, margin: "12px 0", display: "flex", flexDirection: "column", gap: 14, minWidth: 0 };

export default function RoomPage() {
  const { token } = useParams();
  const BASE = process.env.REACT_APP_BACKEND_URL;
  const [v, setV] = useState(null);
  const [error, setError] = useState(null); // {title, body}
  const [busyDoc, setBusyDoc] = useState("");
  const [docError, setDocError] = useState("");
  const fileRef = useRef(null);
  const [up, setUp] = useState(null); // { file, purpose, progress, busy, error }
  const [sentNote, setSentNote] = useState("");
  const [meeting, setMeeting] = useState(null); // { id, note, busy, error }
  const [metNote, setMetNote] = useState("");
  const [rem, setRem] = useState({ busy: false, error: "" });
  const [calError, setCalError] = useState("");
  useMeta("robots", "noindex, nofollow, noarchive");
  useMeta("referrer", "no-referrer");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/api/public/room/${encodeURIComponent(token)}`, { cache: "no-store", credentials: "omit" });
      const b = await r.json().catch(() => ({}));
      if (r.status === 404) { setError(DEAD(b)); return; }
      if (!r.ok) throw new Error("unavailable");
      setV(b);
      setError(null);
    } catch {
      setError({ title: "This page isn't loading", body: "Something went wrong on our side. Try again in a minute." });
    }
  }, [BASE, token]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const prevBg = document.body.style.background;
    const prevTitle = document.title;
    document.body.style.background = p.bg;
    return () => { document.body.style.background = prevBg; document.title = prevTitle; };
  }, []);
  useEffect(() => { if (v) document.title = `${addressLine(v.property)} · Bricks`; }, [v]);

  const download = async (doc) => {
    setBusyDoc(doc.id); setDocError("");
    try {
      const r = await fetch(`${BASE}/api/public/room/${encodeURIComponent(token)}/documents/${encodeURIComponent(doc.id)}`, { cache: "no-store", credentials: "omit" });
      const b = await r.json().catch(() => ({}));
      if (!r.ok || !b.url) {
        if (r.status === 404 && b?.detail?.code === "link_dead") { setError(DEAD(b)); return; }
        throw new Error(b?.detail?.message || "That download didn't start. Try again.");
      }
      window.location.assign(b.url);
    } catch (e) {
      setDocError(e.message || "That download didn't start. Try again.");
    } finally {
      setBusyDoc("");
    }
  };

  const room = `${BASE}/api/public/room/${encodeURIComponent(token)}`;
  const post = async (path, body) => {
    const r = await fetch(`${room}${path}`, { method: "POST", cache: "no-store", credentials: "omit", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const b = await r.json().catch(() => ({}));
    if (r.status === 404 && b?.detail?.code === "link_dead") { setError(DEAD(b)); const e = new Error(""); e.dead = true; throw e; }
    if (!r.ok) throw new Error(b?.detail?.message || "That didn't go through. Try again.");
    return b;
  };

  const pickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setSentNote("");
    setUp({ file, purpose: "", progress: 0, busy: false, error: fileProblem(file) });
  };

  const sendFile = async () => {
    const { file, purpose } = up;
    const type = fileType(file);
    const meta = { name: file.name, content_type: type, size: file.size, condition_id: purpose || null };
    setUp((u) => ({ ...u, busy: true, error: "", progress: 0 }));
    try {
      const start = await post("/uploads", meta);
      try {
        await putFile(start.signed_url, file, type, (n) => setUp((u) => (u ? { ...u, progress: n } : u)));
      } catch {
        throw new Error("The file didn't upload. Check your connection and try again.");
      }
      await post("/uploads/complete", { ...meta, path: start.path });
      setUp(null);
      setSentNote(`Sent. ${firstName} will review it.`);
      load();
    } catch (e) {
      if (e.dead) return;
      setUp((u) => (u ? { ...u, busy: false, error: e.message } : u));
    }
  };

  const sayMet = async (c) => {
    setMeeting((m) => ({ ...m, busy: true, error: "" }));
    try {
      await post(`/conditions/${encodeURIComponent(c.id)}/met`, { note: metNote });
      setMeeting(null); setMetNote("");
      load();
    } catch (e) {
      if (e.dead) return;
      setMeeting((m) => ({ ...m, busy: false, error: e.message }));
    }
  };

  const setReminders = async (on) => {
    setRem({ busy: true, error: "" });
    try {
      const b = await post("/reminders", { on });
      setV((x) => ({ ...x, reminders: b.reminders }));
      setRem({ busy: false, error: "" });
    } catch (e) {
      if (e.dead) return;
      setRem({ busy: false, error: e.message });
    }
  };

  // The feed carries only this party's dates. A subscription keeps up as the deal moves.
  const calUrl = `${room}/calendar.ics`;
  const subscribeUrl = calUrl.replace(/^https?:\/\//, "webcal://");
  const downloadCal = async (e) => {
    if (process.env.REACT_APP_DEMO_MODE !== "true") return; // real builds: the browser follows the link
    e.preventDefault(); setCalError("");
    try {
      const r = await fetch(calUrl, { cache: "no-store", credentials: "omit" });
      if (r.status === 404) { setError(DEAD(await r.json().catch(() => ({})))); return; }
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(new Blob([await r.text()], { type: "text/calendar" }));
      const a = document.createElement("a"); a.href = url; a.download = "deal-dates.ics"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setCalError("That didn't download. Try again.");
    }
  };
  const hasCalendar = v && (v.dates.length > 0 || v.conditions.some((c) => ["open", "due_soon", "overdue"].includes(c.state)));

  const until = v?.link?.expires_at ? fmtInstant(v.link.expires_at) : "";
  const place = v ? [v.property.city, v.property.province].filter(Boolean).join(", ") : "";
  const firstName = v?.agent?.name ? v.agent.name.split(" ")[0] : "the agent";

  return (
    <div data-testid="room-page" style={{ minHeight: "100vh", background: p.bg, color: p.ink, fontFamily: F.display, overflowX: "clip", display: "flex", flexDirection: "column" }}>
      <style>{CSS}</style>
      <header style={{ borderBottom: `1px solid ${p.hair}` }}>
        <div className="rp-wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, height: 72 }}>
          <a href="https://www.brxs.ca" aria-label="Bricks" rel="noreferrer" style={{ display: "inline-flex", minHeight: 44, alignItems: "center", textDecoration: "none" }}>
            <Wordmark size={20} color={T.ink} />
          </a>
          {until ? (
            <span data-testid="room-valid" style={{ fontSize: 13, color: p.ink3, whiteSpace: "nowrap" }}>
              <span className="rp-long">Link valid until {until}</span><span className="rp-short">Valid until {until}</span>
            </span>
          ) : null}
        </div>
      </header>

      <div className="rp-wrap" style={{ flex: 1, width: "100%" }}>
        {error ? (
          <div data-testid="room-page-error" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{ maxWidth: 440, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>{error.title}</h1>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, color: p.ink3 }}>{error.body}</p>
            </div>
          </div>
        ) : !v ? (
          <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <BricksLoader variant="assemble" size={40} color={T.brick} label="Loading your deal page" />
          </div>
        ) : (
          <main>
            <div style={{ padding: "clamp(32px, 6vw, 72px) 0 clamp(28px, 4vw, 48px)" }}>
              <p data-testid="room-for" style={{ margin: "0 0 12px", fontSize: 14, color: p.ink3 }}>
                For {v.party.name}, {v.party.role_label.toLowerCase()}{v.party.company ? ` at ${v.party.company}` : ""}
              </p>
              <h1 style={{ margin: 0, fontWeight: 500, fontSize: "clamp(30px, 5vw, 56px)", lineHeight: 1.05, letterSpacing: "-0.025em", overflowWrap: "anywhere" }}>{addressLine(v.property)}</h1>
              {place ? <p style={{ margin: "12px 0 0", fontSize: 17, color: p.ink2 }}>{place}</p> : null}
            </div>
            <div className="rp-grid" style={{ paddingBottom: 24 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 36, minWidth: 0 }}>
                <RoomConditions v={v} p={p} saidText={`You told ${firstName} it's met`}
                  condAction={(c) => (c.can_say_met && meeting?.id !== c.id ? (
                    <RoomAction p={p} testid={`room-say-met-${c.id}`} label={`Mark ${c.label} as met`} onClick={() => { setMetNote(""); setMeeting({ id: c.id, busy: false, error: "" }); }}>Mark as met</RoomAction>
                  ) : null)}
                  after={(c) => (meeting?.id === c.id ? (
                    <div style={panel} data-testid="room-met-confirm">
                      <div style={{ fontSize: 14, lineHeight: 1.55, color: p.ink }}>
                        Tell {firstName} this condition is met? It doesn't change the deal by itself. {firstName} records the outcome.
                      </div>
                      <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                        <span style={{ fontSize: 13, color: p.ink2 }}>Note (optional)</span>
                        <textarea value={metNote} onChange={(e) => setMetNote(e.target.value)} maxLength={500} rows={3} data-testid="room-met-note"
                          placeholder="e.g. Commitment letter attached" style={{ ...field, padding: "10px 12px", resize: "vertical", lineHeight: 1.5 }} />
                      </label>
                      {meeting.error ? <div role="alert" style={{ fontSize: 13, color: T.brickDeep }}>{meeting.error}</div> : null}
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <Btn variant="primary" onClick={() => sayMet(c)} disabled={meeting.busy} data-testid="room-met-send">{meeting.busy ? "Sending…" : `Tell ${firstName}`}</Btn>
                        <Btn variant="ghost" onClick={() => setMeeting(null)} disabled={meeting.busy}>Cancel</Btn>
                      </div>
                    </div>
                  ) : null)} />
                {v.hand_back ? (
                  <RoomBlock title={`Send to ${firstName}`} p={p} testid="room-send"
                    note={`A document you send goes to ${firstName} to review. Nothing on the deal changes until they accept it.`}>
                    <div style={{ padding: "14px 0", display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
                      <input ref={fileRef} type="file" accept={ACCEPT} onChange={pickFile} style={{ display: "none" }} data-testid="room-file" />
                      {!up ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                          <Btn variant="accent" onClick={() => fileRef.current && fileRef.current.click()} data-testid="room-send-pick">Send a document</Btn>
                          <span style={{ fontSize: 12.5, color: p.ink3 }}>PDF, photo or Word, up to 25 MB</span>
                        </div>
                      ) : (
                        <div style={{ ...panel, margin: 0 }} data-testid="room-send-panel">
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, minWidth: 0 }}>
                            <span style={{ fontSize: 14, color: p.ink, overflowWrap: "anywhere", minWidth: 0 }}>{up.file.name}</span>
                            <span style={{ fontSize: 13, color: p.ink3, whiteSpace: "nowrap" }}>{kb(up.file.size)}</span>
                          </div>
                          {!fileProblem(up.file) ? (
                            <label style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                              <span style={{ fontSize: 13, color: p.ink2 }}>What it's for</span>
                              <select value={up.purpose} onChange={(e) => setUp({ ...up, purpose: e.target.value })} disabled={up.busy} style={field} data-testid="room-send-for">
                                {v.conditions.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                                <option value="">Something else</option>
                              </select>
                            </label>
                          ) : null}
                          {up.busy ? (
                            <div role="status" aria-live="polite" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                              <div style={{ height: 2, background: p.hair }}><div style={{ height: 2, width: `${up.progress}%`, background: p.ink, transition: "width .2s" }} /></div>
                              <span style={{ fontSize: 12.5, color: p.ink3 }}>Sending… {up.progress}%</span>
                            </div>
                          ) : null}
                          {up.error ? <div role="alert" style={{ fontSize: 13, color: T.brickDeep }}>{up.error}</div> : null}
                          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            {fileProblem(up.file)
                              ? <Btn variant="primary" onClick={() => fileRef.current && fileRef.current.click()}>Choose another file</Btn>
                              : <Btn variant="primary" onClick={sendFile} disabled={up.busy} data-testid="room-send-go">{up.busy ? "Sending…" : `Send to ${firstName}`}</Btn>}
                            <Btn variant="ghost" onClick={() => setUp(null)} disabled={up.busy}>Cancel</Btn>
                          </div>
                        </div>
                      )}
                      {sentNote ? <div role="status" data-testid="room-sent-note" style={{ fontSize: 13, color: p.ink2 }}>{sentNote}</div> : null}
                    </div>
                  </RoomBlock>
                ) : null}
                <RoomSent v={v} p={p} title="What you've sent" />
                <RoomDates v={v} p={p} />
                <div>
                  <RoomDocuments v={v} p={p} docAction={(d) => (
                    <RoomAction p={p} onClick={() => download(d)} disabled={!!busyDoc} testid={`room-download-${d.id}`} label={`Download ${d.name}`}>
                      {busyDoc === d.id ? "Opening…" : "Download"}
                    </RoomAction>
                  )} />
                  {docError ? <div role="alert" style={{ fontSize: 13, color: T.brickDeep, marginTop: 10 }}>{docError}</div> : null}
                </div>
              </div>
              <aside style={{ display: "flex", flexDirection: "column", gap: 36, minWidth: 0 }}>
                <RoomAgent v={v} p={p} />
                {hasCalendar ? (
                  <RoomBlock title="Your calendar" p={p} testid="room-calendar">
                    <div style={{ padding: "12px 0 4px", fontSize: 13, lineHeight: 1.6, color: p.ink3 }}>
                      Add your key dates and deadlines to your own calendar. If you subscribe, they stay up to date as the deal moves.
                    </div>
                    <div style={{ display: "flex", gap: 18, flexWrap: "wrap", paddingBottom: 6 }}>
                      <a href={subscribeUrl} rel="noreferrer" data-testid="room-cal-subscribe" style={LINK}>Subscribe</a>
                      <a href={calUrl} rel="noreferrer" onClick={downloadCal} data-testid="room-cal-download" style={LINK}>Download once</a>
                    </div>
                    {calError ? <div role="alert" style={{ fontSize: 13, color: T.brickDeep, paddingBottom: 8 }}>{calError}</div> : null}
                  </RoomBlock>
                ) : null}
                {v.reminders?.available ? (
                  <RoomBlock title="Reminders" p={p} testid="room-reminders">
                    {v.reminders.has_email ? (
                      <RoomLine p={p} testid="room-reminders-line"
                        left="Email me two days before each deadline"
                        sub={v.reminders.on ? `On. Going to ${v.reminders.email_hint}.` : `To ${v.reminders.email_hint}. You can stop any time.`}
                        action={<RoomAction p={p} onClick={() => setReminders(!v.reminders.on)} disabled={rem.busy} testid="room-reminders-toggle">
                          {rem.busy ? "Saving…" : v.reminders.on ? "Turn off" : "Turn on"}
                        </RoomAction>} />
                    ) : (
                      <div data-testid="room-reminders-no-email" style={{ padding: "12px 0", fontSize: 13, lineHeight: 1.6, color: p.ink3 }}>
                        Ask {firstName} to add your email address and you can get an email two days before each deadline.
                      </div>
                    )}
                    {rem.error ? <div role="alert" style={{ fontSize: 13, color: T.brickDeep, padding: "8px 0" }}>{rem.error}</div> : null}
                  </RoomBlock>
                ) : null}
                <RoomBlock title="About this page" p={p} testid="room-about">
                  <div style={{ padding: "12px 0", fontSize: 13, lineHeight: 1.6, color: p.ink3 }}>
                    It shows only what {firstName} chose to share with you, and it updates as the deal moves. The link is just for you, so please don't forward it.{v.hand_back ? ` Anything you send goes only to ${firstName}.` : ""}
                  </div>
                </RoomBlock>
              </aside>
            </div>
          </main>
        )}
      </div>

      <footer style={{ marginTop: 64, borderTop: `1px solid ${p.hair}` }}>
        <div className="rp-wrap" style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12, paddingTop: 20, paddingBottom: 20, minHeight: 72 }}>
          <span style={{ fontSize: 13, color: p.ink3 }}>Shared through Bricks</span>
          <a href="https://www.brxs.ca" rel="noreferrer" style={{ fontSize: 13, color: p.ink2, textDecoration: "none", minHeight: 44, padding: "0 8px", margin: "0 -8px", display: "inline-flex", alignItems: "center" }}>brxs.ca</a>
        </div>
      </footer>
    </div>
  );
}
